import { isDrawableByPdfMake } from "../../infrastructure/documents/pdf-image.js";
import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import type { RequestContext } from "../context/request-context.js";
import type { AvrReportRepository, RecommendationRepository } from "../../repositories/operational-repositories.js";
import type { EngineeringCalculationService } from "./engineering-calculation-service.js";
import type { AvrThicknessSourceAdapter } from "./avr-sources.js";
import type { Api570Inputs, Api570RateBasis } from "../../domain/engineering/api570-calculator.js";
import { yearsBetween } from "../../domain/engineering/api570-calculator.js";
import {
  AVR_CRITICALITY_LABELS,
  avrGeneralStatusLabel,
  AVR_HISTORY_LABELS,
  AVR_SITUATION_LABELS,
  AVR_STATUS_LABELS,
  AVR_POINT_HISTORY_ORIGIN_LABELS,
  effectiveReferencesOf,
  parseThicknessInput,
  suggestedPointHistory,
  validatePointHistoryInput,
  pointHistoryState,
  isAvrLocked,
  isAvrSubmittable,
  validateAvrForSubmission,
  AVR_SOURCE_KIND_LABELS,
  classifyComponent,
  correlationKey,
  findCriticalPoint,
  formatCivilDate,
  formatNumberBR,
  normalizeAvrReport,
  summarizeCriticality,
  validateAvrCompleteness,
  worstCriticality,
  type AvrComponentAnalysis,
  type AvrDocumentModel,
  type AvrHistoryEntry,
  type AvrPlhReview,
  type AvrPointHistory,
  type AvrHistoryReference,
  type AvrHistoryTermInput,
  type AvrEffectiveReference,
  type AvrReport,
  type AvrReportStatus,
  type AvrSignatory,
  type AvrSourceKind,
  type AvrSourceReport,
  type AvrThicknessReading,
  type AvrValidationResult,
  AVR_ENGINEERING_STATUSES,
  type AvrFinalization
} from "../../domain/engineering/avr-report-domain.js";
import { canActAsPlh, isEligibleToBeNamedPlh, isPlhQualifiedProfessional } from "../../domain/identity/plh-eligibility.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { CLIENT_WITHOUT_PLH, type ProjectResponsibilityService } from "./project-responsibility-service.js";
import type { DocumentHoldService } from "./document-hold-service.js";
import type { TechnicalCorrectionService } from "./technical-correction-service.js";
import { selectAvrForResidualLife } from "../../domain/report/avr-residual-life-source.js";
import { notificationService } from "./notification-service.js";
import type { ClientId, EquipmentId } from "../../shared/kernel/identifiers.js";
import type { DocumentNumberAllocator } from "./document-numbering-service.js";
import type { DocumentVisual, TemplatedDocument } from "../../domain/studio/visual-design.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

const randomUUID = () => globalThis.crypto.randomUUID();

const RATE_BASIS_LABELS: Record<Api570RateBasis, string> = {
  HIGHEST: "Maior entre curto e longo prazo (referência conservadora)",
  SHORT_TERM: "Curto prazo",
  LONG_TERM: "Longo prazo",
  MANUAL: "Manual (justificada)"
};

const SIGNATORY_TITLES: Record<AvrSignatory["role"], string> = { ELABORADO_POR: "Elaborado por", PLH: "Engenheiro PLH" };

export interface AvrSeed {
  clientId?: string | null;
  clientDisplayName?: string;
  projectId?: string | null;
  projectContract?: string;
  unitAreaId?: string | null;
  unitArea?: string;
  equipmentId?: string | null;
  equipmentTag?: string;
  equipmentTypeLabel?: string;
  material?: string;
  fluid?: string;
}

export interface AvrSourceFilters {
  kind?: AvrSourceKind | "ALL";
  from?: string;
  to?: string;
}

/** Ref de fonte enviada pela tela: só o tipo e o id — o serviço reimporta tudo do relatório real. */
export interface AvrSourceRef {
  kind: AvrSourceKind;
  reportId: string;
}

/**
 * AVR: fontes só ME/B-Scan; dados importados somente leitura; taxa de
 * corrosão e vida remanescente pedidas ao motor API 570 existente — este
 * serviço não tem fórmula própria.
 */
export class AvrReportService {
  constructor(
    private readonly repo: AvrReportRepository,
    private readonly calculations: EngineeringCalculationService,
    private readonly sources: readonly AvrThicknessSourceAdapter[],
    private readonly identityRepo?: any,
    private readonly recommendationRepo?: RecommendationRepository,
    private readonly audit?: AuditRecorder
  ) {}

  // ------------------------------------------------------------- READ

  async getReport(context: RequestContext, id: string): Promise<AvrReport | null> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const report = await this.repo.findById(context, id);
    return report ? normalizeAvrReport(report) : null;
  }

  async listByEquipment(context: RequestContext, equipmentId: string): Promise<readonly AvrReport[]> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const list = await this.repo.findByEquipment(context, equipmentId);
    return [...list].map(normalizeAvrReport).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  /**
   * Fila do Módulo Engenharia: AVRs do cliente ativo (ou de todos os
   * autorizados quando não há cliente ativo) que estão no workflow do PLH,
   * mais as em elaboração para os cards. Quem lê precisa de AvrRead.
   */
  async listForEngineering(context: RequestContext, clientId?: string | null): Promise<readonly AvrReport[]> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const target = clientId ?? context.activeClientId ?? null;
    const list = target ? await this.repo.listByClient(context, String(target)) : (this.repo.listAll ? await this.repo.listAll() : []);
    return [...list].map(normalizeAvrReport).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  /** Relatórios ME/B-Scan do equipamento — de nenhum outro, e de nenhuma outra natureza. */
  async listAvailableSources(context: RequestContext, clientId: string, equipmentId: string, filters: AvrSourceFilters = {}): Promise<AvrSourceReport[]> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const all: AvrSourceReport[] = [];
    for (const adapter of this.sources) {
      if (filters.kind && filters.kind !== "ALL" && adapter.kind !== filters.kind) continue;
      all.push(...await adapter.listReports(context, clientId, equipmentId));
    }
    return all
      .filter(s => s.equipmentId === equipmentId)
      .filter(s => !filters.from || s.testDate >= filters.from)
      .filter(s => !filters.to || s.testDate <= filters.to)
      .sort((a, b) => a.testDate.localeCompare(b.testDate) || a.reportNumber.localeCompare(b.reportNumber));
  }

  // ------------------------------------------------------------ CREATE

  async createDraft(context: RequestContext, seed: AvrSeed = {}): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrCreate);
    const clientId = (seed.clientId || context.activeClientId) as ClientId;
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, "No client for the AVR", { safeMessage: "Selecione o Cliente antes de criar a AVR." });
    }
    if (!seed.equipmentId) {
      throw new AppError(ErrorCode.Validation, "No equipment for the AVR", { safeMessage: "Selecione o Equipamento / TAG antes de criar a AVR." });
    }

    const now = new Date().toISOString();
    const by = context.userId || "system";
    const avrNumber = await this.generateNumber(context, clientId, { projectId: seed.projectId ?? null, unitAreaId: seed.unitAreaId ?? null });

    const report: AvrReport = {
      id: `avr-${randomUUID()}`,
      clientId,
      avrNumber,
      clientReportNumber: "",
      revision: "00",
      status: "DRAFT",
      projectId: seed.projectId ?? null,
      unitAreaId: seed.unitAreaId ?? null,
      equipmentId: seed.equipmentId as EquipmentId,
      clientDisplayName: seed.clientDisplayName || "",
      projectContract: seed.projectContract || "",
      unitArea: seed.unitArea || "",
      equipmentTag: seed.equipmentTag || "",
      equipmentTypeLabel: seed.equipmentTypeLabel || "",
      material: seed.material || "",
      fluid: seed.fluid || "",
      osWorkOrderId: null,
      osNumber: "",
      osSubWorkOrderId: null,
      subOsNumber: "",
      selectedSources: [],
      readings: [],
      pointHistories: [],
      complementary: { evaluationDate: now.slice(0, 10), criterionNorm: "", minimumThicknessAdoptedMm: null, minimumThicknessOrigin: "", assumptions: "", proposedInspectionInterval: "", observations: "" },
      rateBasis: "HIGHEST",
      rateBasisJustification: "",
      components: [],
      calculatedAt: null,
      calculationOutdated: false,
      analysis: "",
      conclusion: { status: "", observation: "" },
      recommendationIds: [],
      croqui: null,
      signatories: [],
      issueDate: now,
      history: [{ at: now, by, action: "CREATED", detail: `AVR ${avrNumber} criada.` }],
      frozenSnapshot: null,
      plhReview: null,
      engineerResponsible: null,
      signedAt: null,
      signedByUserId: null,
      signedByProfessionalId: null,
      createdAt: now,
      updatedAt: now,
      createdBy: by
    };
    await this.repo.save(context, report);
    return report;
  }

  // -------------------------------------------------------------- SAVE

  /**
   * Salva o rascunho preservando id, número, revisão, criação, histórico e —
   * sempre — fontes, leituras e resultados: esses só mudam pelas operações
   * próprias (selectSources / refreshSources / calculate), nunca por edição.
   */
  async saveDraft(context: RequestContext, report: AvrReport): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrUpdate);
    const existing = await this.requireEditable(context, report.id);
    const now = new Date().toISOString();

    const incoming = normalizeAvrReport({ ...report });
    // Nada mudou (autosave / salvar antes de visualizar): não gera histórico nem mexe no status.
    if (this.editableFingerprint(existing) === this.editableFingerprint(incoming)) return existing;
    const updated: AvrReport = {
      ...incoming,
      id: existing.id,
      clientId: existing.clientId,
      avrNumber: existing.avrNumber,
      revision: existing.revision,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
      // Dados importados e resultados são somente leitura na tela.
      selectedSources: existing.selectedSources,
      readings: existing.readings,
      pointHistories: existing.pointHistories,
      components: existing.components,
      calculatedAt: existing.calculatedAt,
      calculationOutdated: existing.calculationOutdated,
      frozenSnapshot: existing.frozenSnapshot,
      plhReview: existing.plhReview,
      engineerResponsible: existing.engineerResponsible,
      history: this.append(existing.history, { at: now, by: context.userId || "system", action: "SAVED", detail: "Rascunho salvo." }),
      // Editar dados complementares/critério que alimentam o cálculo pede recálculo.
      status: this.statusAfterEdit(existing, incoming),
      issueDate: now,
      updatedAt: now
    };
    // Mudança da mínima adotada ou da base da taxa invalida o cálculo.
    if (existing.calculatedAt && this.calculationInputsChanged(existing, incoming)) {
      updated.calculationOutdated = true;
      if (!this.inCorrection(existing)) updated.status = "OUTDATED";
    }
    this.markCorrectionDone(updated, context, now);
    await this.repo.save(context, updated);
    return updated;
  }

  // ----------------------------------------------------------- SOURCES

  /**
   * Define os relatórios de origem e reimporta as leituras de cada um a
   * partir do relatório real. Qualquer mudança nas fontes marca o cálculo
   * como desatualizado.
   */
  async selectSources(context: RequestContext, id: string, refs: readonly AvrSourceRef[]): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrSelectSources);
    const existing = await this.requireEditable(context, id);
    if (!existing.equipmentId) {
      throw new AppError(ErrorCode.Validation, "AVR without equipment", { safeMessage: "A AVR não possui Equipamento definido." });
    }

    const available = await this.listAvailableSources(context, existing.clientId, String(existing.equipmentId));
    const selected: AvrSourceReport[] = [];
    for (const ref of refs) {
      const source = available.find(s => s.kind === ref.kind && s.reportId === ref.reportId);
      if (!source) {
        throw new AppError(ErrorCode.Validation, `Source ${ref.kind}/${ref.reportId} is not a ME/B-Scan report of this equipment`, {
          safeMessage: "Só Relatórios ME ou B-Scan deste Equipamento podem ser usados como fonte da AVR."
        });
      }
      selected.push(source);
    }

    const readings = await this.importReadings(context, existing.clientId, selected);
    const now = new Date().toISOString();
    const before = existing.selectedSources.map(s => s.reportNumber);
    const after = selected.map(s => s.reportNumber);
    const removed = before.filter(n => !after.includes(n));
    const added = after.filter(n => !before.includes(n));

    const updated: AvrReport = {
      ...existing,
      selectedSources: selected,
      readings,
      calculationOutdated: existing.calculatedAt !== null,
      status: this.inCorrection(existing) ? existing.status : existing.calculatedAt !== null ? "OUTDATED" : "DRAFT",
      history: this.append(existing.history, {
        at: now,
        by: context.userId || "system",
        action: "SOURCES_CHANGED",
        detail: `Fontes: ${after.join(", ") || "nenhuma"}${added.length ? ` · incluídas: ${added.join(", ")}` : ""}${removed.length ? ` · removidas: ${removed.join(", ")}` : ""}.`
      }),
      updatedAt: now
    };
    // OS/Sub-OS herdadas do relatório de origem mais recente quando a AVR ainda não tem as suas.
    const latestWithOs = [...selected].reverse().find(s => s.osNumber);
    if (!updated.osWorkOrderId && !updated.osNumber && latestWithOs) {
      updated.osWorkOrderId = latestWithOs.osWorkOrderId;
      updated.osNumber = latestWithOs.osNumber;
      updated.osSubWorkOrderId = latestWithOs.osSubWorkOrderId;
      updated.subOsNumber = latestWithOs.subOsNumber;
    }
    this.markCorrectionDone(updated, context, now);
    await this.repo.save(context, updated);
    return updated;
  }

  /** Reaproveita o croqui de um relatório de origem selecionado — nunca de outro relatório. */
  async useCroquiFromSource(context: RequestContext, id: string, ref: AvrSourceRef): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrUpdate);
    const existing = await this.requireEditable(context, id);
    const source = existing.selectedSources.find(s => s.kind === ref.kind && s.reportId === ref.reportId);
    if (!source) {
      throw new AppError(ErrorCode.Validation, "Croqui source is not a selected report", { safeMessage: "O croqui só pode vir de um relatório de origem selecionado nesta AVR." });
    }
    const adapter = this.sources.find(a => a.kind === source.kind);
    const image = adapter?.croquiOf ? await adapter.croquiOf(context, existing.clientId, source.reportId) : null;
    if (!image) {
      throw new AppError(ErrorCode.Validation, "Source has no croqui", { safeMessage: `O relatório ${source.reportNumber} não possui croqui.` });
    }
    const updated: AvrReport = {
      ...existing,
      croqui: { image, fileName: `croqui-${source.reportNumber}`, sourceReportNumber: source.reportNumber },
      updatedAt: new Date().toISOString()
    };
    await this.repo.save(context, updated);
    return updated;
  }

  /** Recommendations do módulo oficial para o equipamento da AVR — para vincular, nunca para criar aqui. */
  async listEquipmentRecommendations(context: RequestContext, id: string): Promise<Array<{ id: string; zr: string; scope: string; status: string }>> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const report = await this.requireReport(context, id);
    if (!this.recommendationRepo || !report.equipmentId) return [];
    const list = await this.recommendationRepo.findByEquipment(context, report.equipmentId);
    return list.map(r => ({ id: String(r.id), zr: (r as any).zr || "", scope: (r as any).scope || "", status: String((r as any).status || "") }));
  }

  /** Reimporta as leituras dos mesmos relatórios (revisões atuais) — "ATUALIZAR DADOS DAS FONTES". */
  async refreshSources(context: RequestContext, id: string): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrSelectSources);
    const existing = await this.requireEditable(context, id);
    if (!existing.equipmentId) return existing;
    const available = await this.listAvailableSources(context, existing.clientId, String(existing.equipmentId));
    const refreshed = existing.selectedSources
      .map(s => available.find(a => a.kind === s.kind && a.reportId === s.reportId))
      .filter((s): s is AvrSourceReport => !!s);
    const readings = await this.importReadings(context, existing.clientId, refreshed);
    const now = new Date().toISOString();
    const changed = JSON.stringify(readings) !== JSON.stringify(existing.readings) || refreshed.length !== existing.selectedSources.length;
    const updated: AvrReport = {
      ...existing,
      selectedSources: refreshed,
      readings,
      calculationOutdated: existing.calculationOutdated || (changed && existing.calculatedAt !== null),
      status: changed && existing.calculatedAt !== null && !this.inCorrection(existing) ? "OUTDATED" : existing.status,
      history: this.append(existing.history, { at: now, by: context.userId || "system", action: "SOURCES_REFRESHED", detail: changed ? "Dados das fontes atualizados — cálculo marcado como desatualizado." : "Dados das fontes conferidos — sem alteração." }),
      updatedAt: now
    };
    await this.repo.save(context, updated);
    return updated;
  }

  private async importReadings(context: RequestContext, clientId: string, sources: readonly AvrSourceReport[]): Promise<AvrThicknessReading[]> {
    const readings: AvrThicknessReading[] = [];
    for (const source of sources) {
      const adapter = this.sources.find(a => a.kind === source.kind);
      if (!adapter) continue;
      readings.push(...await adapter.readingsOf(context, clientId, source.reportId));
    }
    return readings.sort((a, b) => a.date.localeCompare(b.date));
  }

  // --------------------------------------------------------- CALCULATE

  /**
   * CALCULAR: correlaciona as leituras por ponto e pede ao motor API 570 —
   * o mesmo da calculadora de Engenharia — a taxa (curto/longo prazo,
   * adotada) e a vida remanescente de cada ponto, com as datas reais dos
   * relatórios. Não há fórmula aqui.
   */
  async calculate(context: RequestContext, id: string): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrCalculate);
    const existing = await this.requireEditable(context, id);
    if (existing.selectedSources.length === 0) {
      throw new AppError(ErrorCode.Validation, "No sources selected", { safeMessage: "Selecione ao menos um Relatório ME ou B-Scan de origem antes de calcular." });
    }

    const groups = new Map<string, AvrThicknessReading[]>();
    for (const reading of existing.readings) {
      const key = correlationKey(reading);
      const list = groups.get(key) ?? [];
      list.push(reading);
      groups.set(key, list);
    }

    const components: AvrComponentAnalysis[] = [];
    for (const [key, list] of groups) {
      const readings = [...list].sort((a, b) => a.date.localeCompare(b.date));
      const current = readings[readings.length - 1]!;
      // Medição ANTERIOR: o histórico informado no AVR (DADO COMPLEMENTAR) tem
      // precedência; senão o relatório anterior correlacionado. Sem nenhum,
      // não há taxa — nunca uma taxa fictícia.
      // CURTO PRAZO (medição anterior) e LONGO PRAZO (medição inicial): o
      // histórico informado no AVR tem precedência; senão os relatórios
      // anteriores correlacionados. Sem nenhum, não há taxa — nunca fictícia.
      const manual = existing.pointHistories.find(h => h.key === key);
      const refs = effectiveReferencesOf(readings, manual);

      // Espessura mínima: a do relatório de origem (menor registrada para o
      // ponto); sem ela, a adotada nos dados complementares, com a origem
      // declarada. Nunca inventada.
      const registeredMin = readings.map(r => r.minMm).filter((v): v is number => v !== null);
      let minimum: number | null = null;
      let minimumOrigin = "";
      if (registeredMin.length > 0) {
        minimum = Math.min(...registeredMin);
        minimumOrigin = `Relatório ${current.sourceKind === "ME" ? "ME" : "B-Scan"} ${current.reportNumber}`;
      } else if (existing.complementary.minimumThicknessAdoptedMm !== null) {
        minimum = existing.complementary.minimumThicknessAdoptedMm;
        minimumOrigin = existing.complementary.minimumThicknessOrigin || "Espessura mínima adotada (dados complementares)";
      }

      let api570 = null;
      if (refs.shortTerm || refs.longTerm) {
        // O motor recebe as três referências (atual, curto, longo). O que faltar
        // vai nulo e o próprio motor devolve a taxa correspondente como não calculada.
        const inputs: Api570Inputs = {
          initialThicknessMm: refs.longTerm ? refs.longTerm.thicknessMm : null,
          initialDate: refs.longTerm ? refs.longTerm.date : "",
          previousThicknessMm: refs.shortTerm ? refs.shortTerm.thicknessMm : null,
          previousDate: refs.shortTerm ? refs.shortTerm.date : "",
          currentThicknessMm: current.thicknessMm,
          currentDate: current.date,
          minimumRequiredThicknessMm: minimum,
          rateBasis: existing.rateBasis,
          rateBasisJustification: existing.rateBasisJustification
        };
        // O motor oficial — previewApi570 não grava registro de calculadora; a AVR guarda o resultado.
        api570 = this.calculations.previewApi570(context, inputs);
      }

      const analysis: AvrComponentAnalysis = {
        key,
        sourceKind: current.sourceKind,
        pointId: current.pointId,
        component: current.component,
        location: current.location,
        readings,
        minimumThicknessMm: minimum,
        minimumThicknessOrigin: minimumOrigin,
        shortTerm: refs.shortTerm,
        longTerm: refs.longTerm,
        api570,
        situation: "INSUFFICIENT_HISTORY",
        criticality: "UNCLASSIFIED"
      };
      const classified = classifyComponent(analysis);
      analysis.situation = classified.situation;
      analysis.criticality = classified.criticality;
      components.push(analysis);
    }
    components.sort((a, b) => a.pointId.localeCompare(b.pointId, "pt-BR", { numeric: true }));

    const now = new Date().toISOString();
    const previousCritical = findCriticalPoint(existing.components);
    const newCritical = findCriticalPoint(components);
    const fmt = (c: AvrComponentAnalysis | null) => c
      ? `${c.pointId}: TC ${formatNumberBR(c.api570?.adoptedRateMmPerYear, 3)} mm/ano, VR ${formatNumberBR(c.api570?.remainingLifeYears, 1)} anos`
      : "—";

    const updated: AvrReport = {
      ...existing,
      components,
      calculatedAt: now,
      calculationOutdated: false,
      status: this.inCorrection(existing) ? existing.status : "CALCULATED",
      history: this.append(existing.history, {
        at: now,
        by: context.userId || "system",
        action: "CALCULATED",
        detail: `Cálculo API 570 em ${components.length} ponto(s). Ponto crítico anterior: ${fmt(previousCritical)}. Novo: ${fmt(newCritical)}.`
      }),
      updatedAt: now
    };
    this.markCorrectionDone(updated, context, now);
    await this.repo.save(context, updated);
    return updated;
  }

  // ---------------------------------------------------------- VALIDATE

  async validate(context: RequestContext, id: string): Promise<AvrValidationResult> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrValidate);
    const report = await this.requireReport(context, id);
    const result = validateAvrCompleteness(report);
    if (!isAvrLocked(report.status)) {
      const now = new Date().toISOString();
      await this.repo.save(context, {
        ...report,
        status: this.inCorrection(report) ? report.status : result.valid ? "VALIDATED" : report.calculationOutdated ? "OUTDATED" : report.calculatedAt ? "CALCULATED" : "DRAFT",
        history: this.append(report.history, { at: now, by: context.userId || "system", action: "VALIDATED", detail: result.valid ? "AVR validada — completa." : `Validação apontou ${result.errors.length} pendência(s).` }),
        updatedAt: now
      });
    }
    return result;
  }

  // ------------------------------------------------------ RECOMMENDATIONS

  /** Vincula Recommendations do módulo oficial (do mesmo equipamento). Não cria recomendação paralela. */
  async linkRecommendations(context: RequestContext, id: string, recommendationIds: readonly string[]): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrUpdate);
    const existing = await this.requireEditable(context, id);
    if (this.recommendationRepo && existing.equipmentId) {
      const ofEquipment = await this.recommendationRepo.findByEquipment(context, existing.equipmentId);
      const allowed = new Set(ofEquipment.map(r => String(r.id)));
      const foreign = recommendationIds.filter(r => !allowed.has(r));
      if (foreign.length > 0) {
        throw new AppError(ErrorCode.Validation, "Recommendation of another equipment", { safeMessage: "Só Recommendations deste Equipamento podem ser vinculadas à AVR." });
      }
    }
    const updated: AvrReport = { ...existing, recommendationIds: [...new Set(recommendationIds)], updatedAt: new Date().toISOString() };
    await this.repo.save(context, updated);
    return updated;
  }

  // ------------------------------------------------------------ DOCUMENT

  async buildDocumentModel(context: RequestContext, id: string): Promise<AvrDocumentModel> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const report = await this.requireReport(context, id);
    if (report.status === "SIGNED" && report.frozenSnapshot) return report.frozenSnapshot;
    return this.composeModel(context, report);
  }

  async buildDocumentSnapshot(context: RequestContext, id: string): Promise<DocumentSnapshot> {
    const model = await this.buildDocumentModel(context, id);
    const report = await this.requireReport(context, id);
    return { id: report.avrNumber, type: "AVR", revision: report.revision, documentNumber: report.avrNumber, clientId: report.clientId, createdAt: new Date(report.issueDate), data: model };
  }

  async recordPdfGenerated(context: RequestContext, id: string): Promise<void> {
    const report = await this.repo.findById(context, id);
    if (!report) return;
    const now = new Date().toISOString();
    await this.repo.save(context, { ...report, history: this.append(report.history, { at: now, by: context.userId || "system", action: "PDF_GENERATED", detail: `PDF gerado (Rev. ${report.revision}).` }) });
  }

  // ---------------------------------------------------------------- SIGN

  /** Assinatura PLH: gate real, sem pendências, e o documento inteiro congelado (fontes, resultados, assinaturas). */
  async signAsPlh(context: RequestContext, id: string): Promise<AvrReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.AvrApprove);
    auth.require(context, PermissionCode.AvrSign);
    const report = await this.requireReport(context, id);
    if (report.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "AVR already signed", { safeMessage: "AVR já assinada." });
    }
    // Assinar é ato posterior à análise: a AVR só se assina com a análise do PLH aprovada.
    if (report.status !== "PLH_APPROVED") {
      throw new AppError(ErrorCode.Conflict, "AVR analysis not approved", { safeMessage: `A AVR só pode ser assinada com a Análise do PLH aprovada (status atual: ${AVR_STATUS_LABELS[report.status]}).` });
    }
    const completeness = validateAvrCompleteness(report);
    if (!completeness.valid) {
      throw new AppError(ErrorCode.Validation, "AVR incomplete", { safeMessage: `A AVR não pode ser assinada com pendências: ${completeness.errors.join(" ")}` });
    }

    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null;
    // Habilitação pelo CLIENTE DO DOCUMENTO: quem o Cliente nomeou responsável.
    const eligibility = canActAsPlh(user, professional, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || "Usuário não habilitado como PLH.", { safeMessage: eligibility.reason || "Você não está habilitado a assinar como PLH." });
    }
    const chosen = report.signatories.find(s => s.role === "PLH" && s.professionalId);
    if (chosen && chosen.professionalId !== professional.id) {
      throw new AppError(ErrorCode.Authorization, "Signer is not the selected PLH", { safeMessage: `O PLH selecionado para esta AVR é ${chosen.name || chosen.professionalId}. Somente ele pode assinar.` });
    }

    const now = new Date().toISOString();
    const signatories = report.signatories.filter(s => s.role !== "PLH");
    signatories.push({ role: "PLH", professionalId: professional.id, name: professional.name || "", functionTitle: professional.functionTitle || SIGNATORY_TITLES.PLH, credential: this.credentialFor(professional), signatureImageId: professional.signatureImageId || null, signedAt: now });

    const signed: AvrReport = { ...report, signatories, status: "SIGNED", signedAt: now, signedByUserId: context.userId || null, signedByProfessionalId: professional.id, issueDate: now, updatedAt: now };
    signed.frozenSnapshot = await this.composeModel(context, signed);
    signed.history = this.append(report.history, { at: now, by: context.userId || "system", action: "SIGNED", detail: `Assinada como PLH por ${professional.name}. Fontes, resultados e assinaturas congelados.` });
    await this.repo.save(context, signed);
    return signed;
  }

  // ------------------------------------------------------------- MODEL

  /**
   * STUDIO INSPECT IA: o visual PUBLICADO entra no modelo documental quando
   * ele é montado. Aprovado/congelado = modelo congelado = visual da época.
   * Sem a ligação (testes, composições antigas), o modelo sai sem visual e o
   * builder imprime o padrão vigente — exatamente como antes.
   */
  private documentVisual: ((type: TemplatedDocument) => Promise<DocumentVisual>) | null = null;

  attachDocumentVisual(provider: (type: TemplatedDocument) => Promise<DocumentVisual>): void {
    this.documentVisual = provider;
  }

  private async composeModel(context: RequestContext, report: AvrReport): Promise<AvrDocumentModel> {
    const rows = report.components.map(c => {
      const current = c.readings[c.readings.length - 1] ?? null;
      const period = c.shortTerm && current ? yearsBetween(c.shortTerm.date, current.date) : null;
      const originLabel = (ref: AvrEffectiveReference | null) => ref ? (ref.origin === "MANUAL" ? AVR_POINT_HISTORY_ORIGIN_LABELS.MANUAL : `${AVR_POINT_HISTORY_ORIGIN_LABELS.SOURCE_REPORT}: ${ref.reference}`) : "";
      return {
        component: [c.pointId, c.component].filter(Boolean).join(" — "),
        location: c.location,
        shortTermThickness: c.shortTerm ? formatNumberBR(c.shortTerm.thicknessMm) : "—",
        shortTermDate: c.shortTerm ? formatCivilDate(c.shortTerm.date) : "—",
        shortTermOrigin: originLabel(c.shortTerm),
        longTermThickness: c.longTerm ? formatNumberBR(c.longTerm.thicknessMm) : "—",
        longTermDate: c.longTerm ? formatCivilDate(c.longTerm.date) : "—",
        longTermOrigin: originLabel(c.longTerm),
        currentReport: current ? current.reportNumber : "—",
        currentDate: current ? formatCivilDate(current.date) : "—",
        currentThickness: current ? formatNumberBR(current.thicknessMm) : "—",
        minimumThickness: formatNumberBR(c.minimumThicknessMm),
        periodYears: formatNumberBR(period, 1),
        longTermRate: formatNumberBR(c.api570?.longTermRateMmPerYear, 3),
        shortTermRate: formatNumberBR(c.api570?.shortTermRateMmPerYear, 3),
        adoptedRate: formatNumberBR(c.api570?.adoptedRateMmPerYear, 3),
        remainingLife: c.situation === "OK" ? formatNumberBR(c.api570?.remainingLifeYears, 1) : "—",
        criticality: c.criticality,
        criticalityLabel: AVR_CRITICALITY_LABELS[c.criticality],
        situationLabel: AVR_SITUATION_LABELS[c.situation]
      };
    });

    const critical = findCriticalPoint(report.components);
    const dates = report.selectedSources.map(s => s.testDate).filter(Boolean).sort();
    const recommendations = await this.loadRecommendations(context, report);

    return {
      visual: this.documentVisual ? await this.documentVisual("AVR").catch(() => null) : null,
      avrNumber: report.avrNumber,
      clientReportNumber: report.clientReportNumber,
      revision: report.revision,
      status: report.status,
      statusLabel: AVR_STATUS_LABELS[report.status] || report.status,
      plhReview: report.plhReview?.approvedAt
        ? { plhName: report.plhReview.approvedByName || report.plhReview.plhName, approvedAt: formatCivilDate(report.plhReview.approvedAt.slice(0, 10)), approvalHash: report.plhReview.approvalHash || "" }
        : null,
      issueDate: formatCivilDate(report.issueDate.slice(0, 10)),
      evaluationDate: formatCivilDate(report.complementary.evaluationDate),
      qrPayload: `INSPECT-IA|AVR|${report.avrNumber}|REV ${report.revision}|${report.id}`,
      header: {
        client: report.clientDisplayName,
        equipmentTag: report.equipmentTag,
        equipmentType: report.equipmentTypeLabel,
        unitArea: report.unitArea,
        osNumber: report.osNumber,
        subOsNumber: report.subOsNumber,
        material: report.material,
        fluid: report.fluid
      },
      summary: summarizeCriticality(report.components),
      components: rows,
      criticalPoint: critical
        ? { component: [critical.pointId, critical.component].filter(Boolean).join(" — "), location: critical.location, remainingLife: formatNumberBR(critical.api570?.remainingLifeYears, 1), adoptedRate: formatNumberBR(critical.api570?.adoptedRateMmPerYear, 3), currentThickness: formatNumberBR(critical.readings[critical.readings.length - 1]?.thicknessMm) }
        : null,
      references: report.selectedSources.map(s => ({ kind: AVR_SOURCE_KIND_LABELS[s.kind], reportNumber: s.reportNumber, revision: s.revision, testDate: formatCivilDate(s.testDate), approved: s.approved })),
      corrosionRate: {
        method: "API 570",
        basisLabel: RATE_BASIS_LABELS[report.rateBasis],
        justification: report.rateBasisJustification,
        period: dates.length >= 2 ? `${formatCivilDate(dates[0])} a ${formatCivilDate(dates[dates.length - 1])}` : dates.length === 1 ? formatCivilDate(dates[0]) : "—",
        reportsUsed: report.selectedSources.map(s => s.reportNumber).join(", ") || "—"
      },
      complementary: { ...report.complementary },
      analysis: report.analysis,
      // O modelo documental leva o RÓTULO ("A CRITÉRIO DO CLIENTE"), não o
      // código ("CRITERIO_CLIENTE"): o PDF imprime o que está aqui.
      conclusion: { status: avrGeneralStatusLabel(report.conclusion.status), worstCondition: AVR_CRITICALITY_LABELS[worstCriticality(report.components)], observation: report.conclusion.observation },
      recommendations,
      croqui: report.croqui,
      signatories: await this.resolveSignatories(report)
    };
  }

  private async loadRecommendations(context: RequestContext, report: AvrReport): Promise<Array<{ zr: string; scope: string }>> {
    if (!this.recommendationRepo || report.recommendationIds.length === 0) return [];
    const out: Array<{ zr: string; scope: string }> = [];
    for (const id of report.recommendationIds) {
      try {
        const rec = await this.recommendationRepo.findById(context, id as any);
        if (rec) out.push({ zr: rec.zr || "", scope: rec.scope || "" });
      } catch { /* recomendação removida: não entra */ }
    }
    return out;
  }

  private async resolveSignatories(report: AvrReport): Promise<AvrDocumentModel["signatories"]> {
    const roles: AvrSignatory["role"][] = ["ELABORADO_POR", "PLH"];
    const byRole = new Map(report.signatories.map(s => [s.role, s]));
    return Promise.all(roles.map(async role => {
      const s = byRole.get(role);
      let signatureImage: string | null = null;
      if (s?.professionalId && this.identityRepo?.getProfessionalSignature) {
        try { signatureImage = await this.identityRepo.getProfessionalSignature(s.professionalId); } catch { signatureImage = null; }
      }
      const date = s?.signedAt || (s?.professionalId ? report.issueDate : "");
      return {
        role,
        roleLabel: role === "ELABORADO_POR" ? "ELABORADO POR" : "ENGENHEIRO PLH",
        name: s?.name || "",
        functionTitle: s?.functionTitle || "",
        credential: s?.credential || "",
        signatureImage: isDrawableByPdfMake(signatureImage) ? signatureImage : null,
        date: date ? formatCivilDate(date.slice(0, 10)) : ""
      };
    }));
  }

  // ------------------------------------------------- HISTÓRICO POR PONTO

  /** Sugestões dos relatórios anteriores correlacionados (curto = imediatamente anterior; longo = mais antigo) + candidatos para "BUSCAR EM RELATÓRIOS ANTERIORES". */
  suggestionFor(report: AvrReport, key: string): ReturnType<typeof suggestedPointHistory> {
    return suggestedPointHistory(report.readings.filter(r => correlationKey(r) === key));
  }

  /** Referências efetivas (informadas ou dos relatórios) e o estado PENDENTE / PARCIAL / COMPLETO de um ponto. */
  historyStateFor(report: AvrReport, key: string): { shortTerm: AvrEffectiveReference | null; longTerm: AvrEffectiveReference | null; state: ReturnType<typeof pointHistoryState>; manual: AvrPointHistory | null } {
    const manual = report.pointHistories.find(h => h.key === key) ?? null;
    const refs = effectiveReferencesOf(report.readings.filter(r => correlationKey(r) === key), manual ?? undefined);
    return { ...refs, state: pointHistoryState(refs), manual };
  }

  /**
   * SALVAR HISTÓRICO do ponto: CURTO PRAZO e LONGO PRAZO, cada um com
   * espessura + data, validados contra a medição atual (LONGO < CURTO <
   * ATUAL). Bloco nulo = não informado (salvar parcial é permitido). Cada
   * campo alterado é auditado separadamente; alterar histórico depois do
   * cálculo marca a AVR como desatualizada.
   */
  async setPointHistory(context: RequestContext, id: string, key: string, input: { shortTerm?: AvrHistoryTermInput | null; longTerm?: AvrHistoryTermInput | null }): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrUpdate);
    const existing = await this.requireEditable(context, id);
    const readings = existing.readings.filter(r => correlationKey(r) === key).sort((a, b) => a.date.localeCompare(b.date));
    const current = readings[readings.length - 1];
    if (!current) {
      throw new AppError(ErrorCode.Validation, "Point not imported", { safeMessage: "O ponto não está entre os dados importados da AVR." });
    }
    const errors = validatePointHistoryInput(input, current.date);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, "Invalid point history", { safeMessage: errors.join(" ") });
    }
    const now = new Date().toISOString();
    const by = context.userId || "system";
    const before = existing.pointHistories.find(h => h.key === key) ?? null;
    const toRef = (term: AvrHistoryTermInput | null | undefined): AvrHistoryReference | null => {
      if (!term) return null;
      const origin = term.origin ?? "MANUAL";
      return { thicknessMm: parseThicknessInput(term.thicknessMm)!, date: (term.date || "").trim(), origin, sourceReference: origin === "SOURCE_REPORT" ? (term.sourceReference || "").trim() : AVR_POINT_HISTORY_ORIGIN_LABELS.MANUAL, updatedAt: now, updatedBy: by };
    };
    const shortTerm = input.shortTerm === undefined ? (before?.shortTerm ?? null) : toRef(input.shortTerm);
    const longTerm = input.longTerm === undefined ? (before?.longTerm ?? null) : toRef(input.longTerm);
    // Só o que mudou entra na trilha e na auditoria — um evento por campo.
    const diffs: Array<{ field: string; label: string; beforeValue: string | number | null; afterValue: string | number | null; display: string }> = [];
    const cmp = (label: string, field: string, b: AvrHistoryReference | null, a: AvrHistoryReference | null) => {
      if ((b?.thicknessMm ?? null) !== (a?.thicknessMm ?? null)) diffs.push({ field: `${field}Thickness`, label: `Espessura ${label}`, beforeValue: b?.thicknessMm ?? null, afterValue: a?.thicknessMm ?? null, display: `Espessura ${label}: ${b ? formatNumberBR(b.thicknessMm) : "—"} → ${a ? formatNumberBR(a.thicknessMm) : "—"} mm` });
      if ((b?.date ?? "") !== (a?.date ?? "")) diffs.push({ field: `${field}Date`, label: `Data ${label}`, beforeValue: b?.date ?? null, afterValue: a?.date ?? null, display: `Data ${label}: ${b ? formatCivilDate(b.date) : "—"} → ${a ? formatCivilDate(a.date) : "—"}` });
      if ((b?.origin ?? null) !== (a?.origin ?? null) || (b?.sourceReference ?? "") !== (a?.sourceReference ?? "")) diffs.push({ field: `${field}Source`, label: `Origem ${label}`, beforeValue: b ? b.sourceReference : null, afterValue: a ? a.sourceReference : null, display: `Origem ${label}: ${a ? a.sourceReference : "—"}` });
    };
    cmp("Curto Prazo", "shortTerm", before?.shortTerm ?? null, shortTerm);
    cmp("Longo Prazo", "longTerm", before?.longTerm ?? null, longTerm);
    if (diffs.length === 0) return existing;

    const entry: AvrPointHistory = { key, pointId: current.pointId, shortTerm, longTerm };
    const updated: AvrReport = {
      ...existing,
      // Os dois blocos limpos = ponto volta a não ter histórico complementar (a entrada sai da lista).
      pointHistories: [...existing.pointHistories.filter(h => h.key !== key), ...(shortTerm || longTerm ? [entry] : [])].sort((a, b) => a.pointId.localeCompare(b.pointId, "pt-BR", { numeric: true })),
      calculationOutdated: existing.calculatedAt !== null,
      status: existing.calculatedAt !== null && !this.inCorrection(existing) ? "OUTDATED" : existing.status,
      history: this.append(existing.history, { at: now, by, action: "POINT_HISTORY_CHANGED", detail: `Ponto ${current.pointId} · ${diffs.map(d => d.display).join(" · ")}`, professionalId: context.professionalId || null }),
      updatedAt: now
    };
    this.markCorrectionDone(updated, context, now);
    await this.repo.save(context, updated);
    if (this.audit) {
      for (const d of diffs) {
        try {
          await this.audit.record(context, {
            action: AuditAction.AvrPointHistoryChanged,
            entityType: "AVR",
            entityId: updated.id,
            previousState: { [d.field]: d.beforeValue } as any,
            newState: { [d.field]: d.afterValue } as any,
            metadata: { avrId: updated.id, avrNumber: updated.avrNumber, point: current.pointId, key, field: d.field, fieldLabel: d.label, previousValue: d.beforeValue, newValue: d.afterValue, performedBy: by, professionalId: context.professionalId || null, at: now } as any
          });
        } catch (e) {
          console.warn("[AVR] Falha ao auditar histórico do ponto", e);
        }
      }
    }
    return updated;
  }

  async clearPointHistory(context: RequestContext, id: string, key: string): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrUpdate);
    const existing = await this.requireEditable(context, id);
    const before = existing.pointHistories.find(h => h.key === key);
    if (!before) return existing;
    const now = new Date().toISOString();
    const updated: AvrReport = {
      ...existing,
      pointHistories: existing.pointHistories.filter(h => h.key !== key),
      calculationOutdated: existing.calculatedAt !== null,
      status: existing.calculatedAt !== null && !this.inCorrection(existing) ? "OUTDATED" : existing.status,
      history: this.append(existing.history, { at: now, by: context.userId || "system", action: "POINT_HISTORY_CHANGED", detail: `Ponto ${before.pointId} · histórico complementar removido (curto e longo prazo).` }),
      updatedAt: now
    };
    await this.repo.save(context, updated);
    return updated;
  }

  // ---------------------------------------------------------- EXCLUIR

  /** Permitida só em elaboração (rascunho/calculado/desatualizado/validado); nunca com o PLH, aprovada ou assinada. */
  canDelete(report: Pick<AvrReport, "status">): boolean {
    return ["DRAFT", "CALCULATED", "OUTDATED", "VALIDATED"].includes(report.status);
  }

  async deleteReport(context: RequestContext, id: string): Promise<void> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrUpdate);
    const report = await this.requireReport(context, id);
    if (!this.canDelete(report)) {
      throw new AppError(ErrorCode.Conflict, "AVR cannot be deleted in this status", { safeMessage: `AVR em "${AVR_STATUS_LABELS[report.status]}" não pode ser excluída. Só rascunhos em elaboração podem ser excluídos.` });
    }
    if (!this.repo.delete) {
      throw new AppError(ErrorCode.Internal, "Repository cannot delete", { safeMessage: "Exclusão não disponível neste armazenamento." });
    }
    await this.repo.delete(context, id);
    if (this.audit) {
      try {
        await this.audit.record(context, {
          action: AuditAction.AvrDeleted,
          entityType: "AVR",
          entityId: report.id,
          previousState: { status: report.status, avrNumber: report.avrNumber } as any,
          newState: null,
          metadata: { avrId: report.id, avrNumber: report.avrNumber, performedBy: context.userId || "system", professionalId: context.professionalId || null, at: new Date().toISOString(), previousStatus: report.status } as any
        });
      } catch (e) {
        console.warn("[AVR] Falha ao auditar exclusão", e);
      }
    }
  }

  // ------------------------------------------------------ WORKFLOW PLH

  /** Validação completa exigida antes do envio — a mesma que o envio executa de novo. */
  async validateForSubmission(context: RequestContext, id: string): Promise<AvrValidationResult> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    const report = await this.requireReport(context, id);
    return validateAvrForSubmission(report);
  }

  /** PLHs habilitados pelo gate real para o Cliente da AVR — a única lista de onde o responsável pode sair. */
  async listPlhCandidates(context: RequestContext, clientId: string): Promise<Array<{ id: string; name: string; functionTitle: string; credential: string; userId: string | null }>> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRead);
    if (!this.identityRepo?.listProfessionals) return [];
    const all: any[] = await this.identityRepo.listProfessionals();
    return all
      // Seletor: oferece quem PODE ser nomeado. Quem É o responsável é do Cliente.
      .filter(p => isEligibleToBeNamedPlh(p).allowed)
      .map(p => ({ id: String(p.id), name: p.name || "", functionTitle: p.functionTitle || "", credential: this.credentialFor(p), userId: p.userId ? String(p.userId) : null }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }

  /**
   * ENVIAR PARA ANÁLISE DO PLH. O Engenheiro escolhe o PLH entre os habilitados
   * (nunca se autoatribui); a AVR passa a AGUARDANDO ANÁLISE DO PLH e os dados
   * técnicos ficam bloqueados até o PLH devolver ou pedir correção.
   */
  /** Responsáveis pelo Projeto (fonte de verdade do PLH) e HOLD/correções (gate da finalização). */
  private responsibility: ProjectResponsibilityService | null = null;
  private holds: DocumentHoldService | null = null;
  private corrections: TechnicalCorrectionService | null = null;

  attachPanelServices(input: { responsibility?: ProjectResponsibilityService; holds?: DocumentHoldService; corrections?: TechnicalCorrectionService }): void {
    if (input.responsibility) this.responsibility = input.responsibility;
    if (input.holds) this.holds = input.holds;
    if (input.corrections) this.corrections = input.corrections;
  }

  /** PLH da AVR resolvido pelo Projeto — o modal de envio mostra em leitura. */
  async resolvePlhFor(context: RequestContext, id: string): Promise<{ plh: { professionalId: string; name: string; functionTitle: string; credential: string } | null; engineer: { professionalId: string; name: string } | null; project: { id: string; code: string; name: string } | null; reason: string }> {
    const report = await this.requireReport(context, id);
    if (!this.responsibility) return { plh: null, engineer: null, project: null, reason: "Resolução de responsáveis pelo Projeto não configurada." };
    const [plh, eng] = await Promise.all([this.responsibility.resolveProjectPlh(context, report.projectId), this.responsibility.resolveProjectEngineer(context, report.projectId)]);
    const project = plh.project || eng.project;
    return {
      plh: plh.responsible && !plh.reason ? { professionalId: plh.responsible.professionalId, name: plh.responsible.name, functionTitle: plh.responsible.functionTitle, credential: plh.responsible.credential } : null,
      engineer: eng.responsible ? { professionalId: eng.responsible.professionalId, name: eng.responsible.name } : null,
      project: project ? { id: project.id, code: project.code, name: project.name } : null,
      reason: plh.reason
    };
  }

  /** AVRs do equipamento/contexto que a Engenharia acompanha (todos os estados — só a excluída não existe mais). */
  async findRelatedAvrsForEngineering(context: RequestContext, equipmentId: string | null, projectId?: string | null): Promise<AvrReport[]> {
    if (!equipmentId) return [];
    const list = await this.listByEquipment(context, equipmentId);
    return list.filter(a => AVR_ENGINEERING_STATUSES.includes(a.status)).filter(a => !projectId || !a.projectId || a.projectId === projectId);
  }

  /** Só AVR tecnicamente válida/aprovada alimenta a Vida Residual automaticamente. */
  async findValidAvrForResidualLife(context: RequestContext, equipmentId: string | null, projectId?: string | null): Promise<AvrReport | null> {
    const related = await this.findRelatedAvrsForEngineering(context, equipmentId, projectId);
    return selectAvrForResidualLife(related, equipmentId);
  }

  /** INSPECT IA REVIEW — revisão IA obrigatória antes de enviar a AVR ao PLH. */
  private aiReviewGate: ((context: RequestContext, documentId: string) => Promise<void>) | null = null;

  attachAiReviewGate(gate: (context: RequestContext, documentId: string) => Promise<void>): void {
    this.aiReviewGate = gate;
  }

  async submitForPlhReview(context: RequestContext, id: string, input: { plhProfessionalId?: string } = {}): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrSubmitForPlhReview);
    const report = await this.requireReport(context, id);
    if (!isAvrSubmittable(report.status)) {
      throw new AppError(ErrorCode.Conflict, "AVR not submittable", { safeMessage: `A AVR não pode ser enviada no status "${AVR_STATUS_LABELS[report.status]}".` });
    }
    const validation = validateAvrForSubmission(report);
    if (!validation.valid) {
      throw new AppError(ErrorCode.Validation, "AVR not ready for PLH review", { safeMessage: `A AVR não pode ser enviada: ${validation.errors.join(" ")}` });
    }
    if (this.aiReviewGate) await this.aiReviewGate(context, report.id);
    // PLH RESPONSÁVEL vem do Projeto (fonte de verdade); sem resolução configurada aceita o id informado (legado).
    let plhId = (input.plhProfessionalId || "").trim();
    if (this.responsibility) plhId = (await this.responsibility.assertProjectHasValidPlh(context, report.projectId)).professionalId;
    if (!plhId) {
      throw new AppError(ErrorCode.Validation, "PLH not selected", { safeMessage: CLIENT_WITHOUT_PLH });
    }
    const plh = this.identityRepo ? await this.identityRepo.getProfessional(plhId) : null;
    if (!plh) {
      throw new AppError(ErrorCode.Validation, "PLH professional not found", { safeMessage: "O Profissional selecionado como PLH não foi encontrado no cadastro." });
    }
    const gate = isPlhQualifiedProfessional(plh, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!gate.allowed) {
      throw new AppError(ErrorCode.Validation, "Professional is not a PLH for this client", { safeMessage: `O Profissional selecionado não está habilitado como PLH para este Cliente: ${gate.reason || ""}` });
    }

    const now = new Date().toISOString();
    const engineer = await this.actorOf(context);
    const isResubmission = report.plhReview !== null;
    const previousStatus = report.status;
    const signatories = report.signatories.filter(s => s.role !== "PLH");
    signatories.push({ role: "PLH", professionalId: plh.id, name: plh.name || "", functionTitle: plh.functionTitle || SIGNATORY_TITLES.PLH, credential: this.credentialFor(plh), signatureImageId: plh.signatureImageId || null, signedAt: null });

    const submitted: AvrReport = {
      ...report,
      signatories,
      status: "AWAITING_PLH_REVIEW",
      engineerResponsible: { userId: engineer.userId, professionalId: engineer.professionalId as any, name: engineer.name },
      plhReview: {
        cycle: (report.plhReview?.cycle ?? 0) + 1,
        plhProfessionalId: plh.id,
        plhName: plh.name || "",
        submittedAt: now,
        submittedByUserId: engineer.userId,
        submittedByProfessionalId: engineer.professionalId as any,
        submittedByName: engineer.name,
        startedAt: null,
        correction: null,
        approvedAt: null,
        approvedByUserId: null,
        approvedByProfessionalId: null,
        approvedByName: "",
        approvedRevision: null,
        approvalHash: null,
        approvalComment: "",
        snapshotAtSubmission: null
      },
      updatedAt: now
    };
    submitted.plhReview!.snapshotAtSubmission = await this.composeModel(context, submitted);
    submitted.history = this.append(report.history, {
      at: now, by: engineer.userId, action: isResubmission ? "RESUBMITTED_FOR_PLH_REVIEW" : "SUBMITTED_FOR_PLH_REVIEW",
      detail: `${isResubmission ? "Reenviada" : "Enviada"} para análise do PLH ${plh.name} por ${engineer.name}. ${report.selectedSources.length} relatório(s) de origem, ${report.components.length} ponto(s).`,
      fromStatus: previousStatus, toStatus: "AWAITING_PLH_REVIEW", professionalId: engineer.professionalId
    });
    await this.repo.save(context, submitted);
    await this.recordAudit(context, isResubmission ? AuditAction.AvrResubmittedForPlhReview : AuditAction.AvrSubmittedForPlhReview, report, submitted, { plhProfessionalId: plh.id, plhName: plh.name || "", cycle: submitted.plhReview!.cycle });
    await this.notify(plh.userId, "AVR_SUBMITTED_FOR_PLH_REVIEW", "AVR aguardando sua análise", `A ${submitted.avrNumber} (${submitted.equipmentTag}) foi ${isResubmission ? "reenviada" : "enviada"} por ${engineer.name} para análise do PLH.`, submitted, `AVR_SUBMIT_${submitted.id}_${submitted.plhReview!.cycle}`);
    return submitted;
  }

  /** ANALISAR: o PLH designado abre a AVR — AGUARDANDO → EM ANÁLISE DO PLH. Idempotente. */
  async startPlhReview(context: RequestContext, id: string): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrPlhReview);
    const report = await this.requireReport(context, id);
    if (report.status === "PLH_REVIEWING") return report;
    if (report.status !== "AWAITING_PLH_REVIEW") {
      throw new AppError(ErrorCode.Conflict, "AVR not awaiting PLH review", { safeMessage: `A AVR não está aguardando análise (status atual: ${AVR_STATUS_LABELS[report.status]}).` });
    }
    const { professional } = await this.requireAssignedPlh(context, report);
    const now = new Date().toISOString();
    const updated: AvrReport = {
      ...report,
      status: "PLH_REVIEWING",
      plhReview: { ...report.plhReview!, startedAt: now },
      history: this.append(report.history, { at: now, by: context.userId || "system", action: "PLH_REVIEW_STARTED", detail: `Análise iniciada pelo PLH ${professional.name}.`, fromStatus: "AWAITING_PLH_REVIEW", toStatus: "PLH_REVIEWING", professionalId: String(professional.id) }),
      updatedAt: now
    };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.AvrPlhReviewStarted, report, updated, { plhProfessionalId: String(professional.id) });
    return updated;
  }

  /** SOLICITAR CORREÇÃO: motivo obrigatório; a AVR volta ao Engenheiro responsável, desbloqueada, no mesmo avrId. */
  async requestCorrection(context: RequestContext, id: string, input: { reason: string; points?: string }): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRequestCorrection);
    const report = await this.requireReport(context, id);
    const reason = (input.reason || "").trim();
    if (!reason) {
      throw new AppError(ErrorCode.Validation, "Correction reason required", { safeMessage: "Informe o motivo / orientação do PLH para a correção." });
    }
    if (!["AWAITING_PLH_REVIEW", "PLH_REVIEWING", "PLH_APPROVED"].includes(report.status)) {
      throw new AppError(ErrorCode.Conflict, "AVR not under PLH review", { safeMessage: `Não é possível solicitar correção no status "${AVR_STATUS_LABELS[report.status]}".` });
    }
    const { professional } = await this.requireAssignedPlh(context, report);
    const now = new Date().toISOString();
    const previousStatus = report.status;
    const updated: AvrReport = {
      ...report,
      status: "CORRECTION_REQUESTED",
      plhReview: {
        ...report.plhReview!,
        approvedAt: null, approvedByUserId: null, approvedByProfessionalId: null, approvedByName: "", approvedRevision: null, approvalHash: null, approvalComment: "",
        correction: { reason, points: (input.points || "").trim(), requestedAt: now, requestedByUserId: context.userId || "system", requestedByProfessionalId: professional.id, requestedByName: professional.name || "", correctedAt: null }
      },
      history: this.append(report.history, { at: now, by: context.userId || "system", action: "PLH_CORRECTION_REQUESTED", detail: `Motivo: ${reason}${input.points?.trim() ? ` · Pontos/campos: ${input.points.trim()}` : ""}`, fromStatus: previousStatus, toStatus: "CORRECTION_REQUESTED", professionalId: String(professional.id) }),
      updatedAt: now
    };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.AvrPlhCorrectionRequested, report, updated, { reason, points: input.points || "", plhProfessionalId: String(professional.id) });
    await this.notify(report.engineerResponsible?.userId ?? null, "AVR_CORRECTION_REQUESTED", "Correção solicitada pelo PLH", `A ${report.avrNumber} (${report.equipmentTag}) voltou para correção. Motivo: ${reason}`, updated, `AVR_CORR_${report.id}_${now}`);
    return updated;
  }

  /** DEVOLVER: o PLH devolve à elaboração sem pedido de correção específico — o ciclo de análise se encerra. */
  async returnToElaboration(context: RequestContext, id: string, reason = ""): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRequestCorrection);
    const report = await this.requireReport(context, id);
    if (!["AWAITING_PLH_REVIEW", "PLH_REVIEWING"].includes(report.status)) {
      throw new AppError(ErrorCode.Conflict, "AVR not under PLH review", { safeMessage: `Não é possível devolver no status "${AVR_STATUS_LABELS[report.status]}".` });
    }
    const { professional } = await this.requireAssignedPlh(context, report);
    const now = new Date().toISOString();
    const previousStatus = report.status;
    const target: AvrReportStatus = report.calculationOutdated ? "OUTDATED" : report.calculatedAt ? "CALCULATED" : "DRAFT";
    const updated: AvrReport = {
      ...report,
      status: target,
      history: this.append(report.history, { at: now, by: context.userId || "system", action: "PLH_RETURNED", detail: reason.trim() ? `Devolvida à elaboração pelo PLH ${professional.name}. ${reason.trim()}` : `Devolvida à elaboração pelo PLH ${professional.name}.`, fromStatus: previousStatus, toStatus: target, professionalId: String(professional.id) }),
      updatedAt: now
    };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.AvrPlhReturned, report, updated, { reason: reason.trim(), plhProfessionalId: String(professional.id) });
    await this.notify(report.engineerResponsible?.userId ?? null, "AVR_CORRECTION_REQUESTED", "AVR devolvida pelo PLH", `A ${report.avrNumber} (${report.equipmentTag}) foi devolvida à elaboração.${reason.trim() ? " " + reason.trim() : ""}`, updated, `AVR_RET_${report.id}_${now}`);
    return updated;
  }

  /**
   * VALIDAR / APROVAR ANÁLISE: registra PLH, data/hora, revisão e o SHA-256 do
   * documento inteiro. Não assina o documento — a assinatura é o passo seguinte.
   */
  async approvePlhReview(context: RequestContext, id: string, input: { comment?: string } = {}): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrApprovePlhReview);
    const report = await this.requireReport(context, id);
    if (!["AWAITING_PLH_REVIEW", "PLH_REVIEWING"].includes(report.status)) {
      throw new AppError(ErrorCode.Conflict, "AVR not under PLH review", { safeMessage: `Não é possível aprovar a análise no status "${AVR_STATUS_LABELS[report.status]}".` });
    }
    const { professional } = await this.requireAssignedPlh(context, report);
    const validation = validateAvrForSubmission(report);
    if (!validation.valid) {
      throw new AppError(ErrorCode.Validation, "AVR incomplete", { safeMessage: `A análise não pode ser aprovada com pendências: ${validation.errors.join(" ")}` });
    }
    const now = new Date().toISOString();
    const previousStatus = report.status;
    const approved: AvrReport = {
      ...report,
      status: "PLH_APPROVED",
      plhReview: {
        ...report.plhReview!,
        startedAt: report.plhReview!.startedAt || now,
        approvedAt: now,
        approvedByUserId: context.userId || null,
        approvedByProfessionalId: professional.id,
        approvedByName: professional.name || "",
        approvedRevision: report.revision,
        approvalComment: (input.comment || "").trim(),
        approvalHash: null
      },
      updatedAt: now
    };
    const model = await this.composeModel(context, approved);
    approved.plhReview!.approvalHash = await this.sha256(JSON.stringify(model));
    approved.history = this.append(report.history, { at: now, by: context.userId || "system", action: "PLH_REVIEW_APPROVED", detail: `Análise aprovada pelo PLH ${professional.name} (Rev. ${report.revision}, hash ${approved.plhReview!.approvalHash.slice(0, 12)}…).${input.comment?.trim() ? ` ${input.comment.trim()}` : ""}`, fromStatus: previousStatus, toStatus: "PLH_APPROVED", professionalId: String(professional.id) });
    await this.repo.save(context, approved);
    await this.recordAudit(context, AuditAction.AvrPlhReviewApproved, report, approved, { plhProfessionalId: String(professional.id), revision: report.revision, hash: approved.plhReview!.approvalHash, comment: (input.comment || "").trim() });
    await this.notify(report.engineerResponsible?.userId ?? null, "AVR_PLH_REVIEW_APPROVED", "Análise do PLH aprovada", `A análise da ${report.avrNumber} (${report.equipmentTag}) foi aprovada pelo PLH ${professional.name}.`, approved, `AVR_APPR_${report.id}_${now}`);
    return approved;
  }

  /** REPROVAR AVR: motivo obrigatório; registra PLH/Engenheiro, data/hora, motivo e revisão. A AVR não é excluída. */
  async rejectPlhReview(context: RequestContext, id: string, input: { reason: string }): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrRequestCorrection);
    const report = await this.requireReport(context, id);
    const reason = (input.reason || "").trim();
    if (!reason) throw new AppError(ErrorCode.Validation, "Reason required", { safeMessage: "Informe o motivo da reprovação." });
    if (!["AWAITING_PLH_REVIEW", "PLH_REVIEWING", "PLH_APPROVED"].includes(report.status)) {
      throw new AppError(ErrorCode.Conflict, "AVR not under PLH review", { safeMessage: `Não é possível reprovar no status "${AVR_STATUS_LABELS[report.status]}".` });
    }
    const { professional } = await this.requireAssignedPlh(context, report);
    const now = new Date().toISOString();
    const previousStatus = report.status;
    const rejected: AvrReport = {
      ...report,
      status: "REJECTED",
      rejection: { rejectedAt: now, rejectedByUserId: context.userId || "system", rejectedByProfessionalId: String(professional.id), rejectedByName: professional.name || "", reason, revision: report.revision },
      history: this.append(report.history, { at: now, by: context.userId || "system", action: "PLH_REJECTED", detail: `AVR reprovada pelo PLH ${professional.name} (Rev. ${report.revision}). Motivo: ${reason}`, fromStatus: previousStatus, toStatus: "REJECTED", professionalId: String(professional.id) }),
      updatedAt: now
    };
    await this.repo.save(context, rejected);
    await this.recordAudit(context, AuditAction.AvrRejected, report, rejected, { reason, plhProfessionalId: String(professional.id), revision: report.revision });
    await this.notify(report.engineerResponsible?.userId ?? null, "AVR_CORRECTION_REQUESTED", "AVR reprovada pelo PLH", `A ${report.avrNumber} (${report.equipmentTag}) foi REPROVADA pelo PLH ${professional.name}. Motivo: ${reason}`, rejected, `AVR_REJ_${report.id}_${now}`);
    return rejected;
  }

  /** Motivos que impedem FINALIZAR RELATÓRIO (vazio = pode finalizar). */
  async finalizationGate(context: RequestContext, report: AvrReport): Promise<{ allowed: boolean; reasons: string[] }> {
    const reasons: string[] = [];
    if (report.finalization) reasons.push("a AVR já está finalizada");
    if (report.status !== "PLH_APPROVED" && report.status !== "SIGNED") reasons.push("a análise de Engenharia/PLH ainda não foi aprovada");
    if (report.calculatedAt === null || report.calculationOutdated) reasons.push("os cálculos não estão válidos (execute CALCULAR)");
    if (this.corrections) {
      const pending = await this.corrections.listPendingByDocument(context, "AVR", report.id);
      if (pending.length > 0) reasons.push(`existem ${pending.length} correção(ões) pendente(s)`);
    }
    if (this.holds) {
      const hold = await this.holds.activeHold(context, "AVR", report.id);
      if (hold) reasons.push("o documento está EM HOLD");
    }
    return { allowed: reasons.length === 0, reasons };
  }

  /** FINALIZAR RELATÓRIO (AVR): snapshot final (hash), bloqueio de edição ordinária, auditoria. Não assina. */
  async finalize(context: RequestContext, id: string): Promise<AvrReport> {
    new RbacAuthorizationService().require(context, PermissionCode.AvrValidate);
    const report = await this.requireReport(context, id);
    const gate = await this.finalizationGate(context, report);
    if (!gate.allowed) throw new AppError(ErrorCode.Conflict, "Cannot finalize", { safeMessage: `Não é possível finalizar: ${gate.reasons.join("; ")}.` });
    const now = new Date().toISOString();
    const actor = await this.actorOf(context);
    const model = await this.composeModel(context, report);
    const finalization: AvrFinalization = { finalizedAt: now, finalizedByUserId: actor.userId, finalizedByName: actor.name, revision: report.revision, snapshotHash: await this.sha256(JSON.stringify(model)), state: report.status === "SIGNED" ? "FINALIZADO" : "FINALIZADO_AGUARDANDO_ASSINATURA" };
    const finalized: AvrReport = { ...report, finalization, history: this.append(report.history, { at: now, by: actor.userId, action: "FINALIZED", detail: `Relatório finalizado por ${actor.name} (Rev. ${report.revision}, hash ${finalization.snapshotHash.slice(0, 12)}…). ${finalization.state === "FINALIZADO" ? "Documento assinado." : "Aguardando assinatura."}`, professionalId: actor.professionalId as any }), updatedAt: now };
    await this.repo.save(context, finalized);
    await this.recordAudit(context, AuditAction.DocumentFinalized, report, finalized, { revision: report.revision, hash: finalization.snapshotHash, state: finalization.state, finalizedBy: actor.userId });
    return finalized;
  }

  /** Linha do tempo pronta para a tela — rótulos em português, sem enum. */
  timelineOf(report: AvrReport): Array<{ at: string; label: string; detail: string; by: string }> {
    return [...report.history].map(h => ({ at: h.at, label: AVR_HISTORY_LABELS[h.action] || h.action, detail: h.detail, by: h.by }));
  }

  // ------------------------------------------------------ WORKFLOW HELPERS

  /** Só o PLH designado age sobre a AVR — e só se ainda passa pelo gate real. */
  private async requireAssignedPlh(context: RequestContext, report: AvrReport): Promise<{ user: any; professional: any }> {
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null;
    // Habilitação pelo CLIENTE DO DOCUMENTO: quem o Cliente nomeou responsável.
    const eligibility = canActAsPlh(user, professional, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || "Not a PLH", { safeMessage: eligibility.reason || "Você não está habilitado como PLH para este Cliente." });
    }
    if (!report.plhReview || String(report.plhReview.plhProfessionalId) !== String(professional.id)) {
      throw new AppError(ErrorCode.Authorization, "Not the assigned PLH", { safeMessage: `Esta AVR está atribuída ao PLH ${report.plhReview?.plhName || "—"}. Somente ele pode analisá-la.` });
    }
    return { user, professional };
  }

  private async actorOf(context: RequestContext): Promise<{ userId: string; professionalId: string | null; name: string }> {
    const userId = context.userId || "system";
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId).catch(() => null) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId).catch(() => null) : null;
    return { userId, professionalId: professional ? String(professional.id) : (user?.professionalId ? String(user.professionalId) : null), name: professional?.name || user?.name || userId };
  }

  private inCorrection(report: AvrReport): boolean {
    return report.status === "CORRECTION_REQUESTED";
  }

  /** Primeira alteração depois do pedido de correção entra na linha do tempo como "Correção realizada". */
  private markCorrectionDone(report: AvrReport, context: RequestContext, now: string): void {
    const correction = report.plhReview?.correction;
    if (report.status !== "CORRECTION_REQUESTED" || !correction || correction.correctedAt) return;
    report.plhReview = { ...report.plhReview!, correction: { ...correction, correctedAt: now } };
    report.history = this.append(report.history, { at: now, by: context.userId || "system", action: "CORRECTION_DONE", detail: "Correção realizada pelo Engenheiro após solicitação do PLH." });
  }

  private async recordAudit(context: RequestContext, action: AuditAction, before: AvrReport, after: AvrReport, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: "AVR",
        entityId: after.id,
        previousState: { status: before.status, revision: before.revision } as any,
        newState: { status: after.status, revision: after.revision } as any,
        metadata: { avrId: after.id, avrNumber: after.avrNumber, revision: after.revision, performedBy: context.userId || "system", professionalId: context.professionalId || null, plh: after.plhReview?.plhProfessionalId || null, at: after.updatedAt, previousStatus: before.status, newStatus: after.status, ...metadata } as any
      });
    } catch (e) {
      console.warn("[AVR] Falha ao registrar auditoria", e);
    }
  }

  /** Notificação interna pelo mecanismo já existente — nunca bloqueia a operação. */
  private async notify(userId: string | null | undefined, event: any, title: string, message: string, report: AvrReport, idempotencyKey: string): Promise<void> {
    if (!userId) return;
    try {
      await notificationService.createNotification({ userId: String(userId), type: "INFO", event, title, message, entityType: "AVR", entityId: report.id, clientId: String(report.clientId), idempotencyKey });
    } catch (e) {
      console.warn("[AVR] Falha ao notificar", e);
    }
  }

  private async sha256(text: string): Promise<string> {
    const subtle = (globalThis as any).crypto?.subtle;
    if (!subtle) return `plain:${text.length}`;
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
  }

  // ------------------------------------------------------------- HELPERS

  /** Uma AVR validada que é editada volta a precisar de validação. */
  private statusAfterEdit(existing: AvrReport, incoming: AvrReport): AvrReport["status"] {
    void incoming;
    if (existing.status === "VALIDATED") return existing.calculatedAt ? "CALCULATED" : "DRAFT";
    return existing.status;
  }

  /** Só o que a tela edita — fontes, leituras, resultados e metadados ficam de fora. */
  private editableFingerprint(r: AvrReport): string {
    return JSON.stringify({
      clientReportNumber: r.clientReportNumber, material: r.material, fluid: r.fluid,
      osWorkOrderId: r.osWorkOrderId, osNumber: r.osNumber, osSubWorkOrderId: r.osSubWorkOrderId, subOsNumber: r.subOsNumber,
      complementary: r.complementary, rateBasis: r.rateBasis, rateBasisJustification: r.rateBasisJustification,
      analysis: r.analysis, conclusion: r.conclusion, recommendationIds: r.recommendationIds, croqui: r.croqui, signatories: r.signatories
    });
  }

  private calculationInputsChanged(existing: AvrReport, incoming: AvrReport): boolean {
    return existing.rateBasis !== incoming.rateBasis ||
      existing.complementary.minimumThicknessAdoptedMm !== incoming.complementary.minimumThicknessAdoptedMm;
  }

  private credentialFor(p: any): string {
    return p.crea || p.cft || p.snqc || p.sncc || p.endIso9712 || p.endIso || "";
  }

  private append(history: AvrHistoryEntry[] | undefined, entry: AvrHistoryEntry): AvrHistoryEntry[] {
    return [...(Array.isArray(history) ? history : []), entry];
  }

  private async requireReport(context: RequestContext, id: string): Promise<AvrReport> {
    const report = await this.repo.findById(context, id);
    if (!report) throw new AppError(ErrorCode.NotFound, "AVR not found", { safeMessage: "AVR não encontrada." });
    return normalizeAvrReport(report);
  }

  private async requireEditable(context: RequestContext, id: string): Promise<AvrReport> {
    const report = await this.requireReport(context, id);
    if (report.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "Signed AVR cannot change", { safeMessage: "AVR assinada não pode mais ser alterada. Crie uma nova revisão para uma nova análise." });
    }
    if (isAvrLocked(report.status)) {
      throw new AppError(ErrorCode.Conflict, "AVR locked for PLH review", { safeMessage: `AVR em "${AVR_STATUS_LABELS[report.status]}": os dados técnicos estão bloqueados. Alterações só pelo fluxo de correção (o PLH solicita correção ou devolve a revisão).` });
    }
    return report;
  }

  /**
   * CONFIGURAÇÕES → NUMERAÇÃO. Com o alocador ligado, o número vem do modelo
   * configurado e de uma sequência ATÔMICA; sem ele (testes, composições que
   * não o fornecem), vale o gerador histórico.
   */
  private numbering: DocumentNumberAllocator | null = null;

  attachNumberAllocator(allocator: DocumentNumberAllocator): void {
    this.numbering = allocator;
  }

  private async generateNumber(context: RequestContext, clientId: string, refs: { projectId?: string | null; unitAreaId?: string | null } = {}): Promise<string> {
    const all = await this.repo.listByClient(context, clientId);
    const year = new Date().getFullYear();
    const prefix = `AVR-${year}-`;
    const count = all.filter(r => r.avrNumber.startsWith(prefix)).length;
    if (this.numbering) {
      return this.numbering.allocate({
        documentType: "AVR",
        clientId, projectId: refs.projectId ?? null, unitAreaId: refs.unitAreaId ?? null,
        legacyCount: async () => count,
        isTaken: async (candidate, scope) => {
          const pool = scope.perClient || !this.repo.listAll ? await this.repo.listByClient(context, clientId) : await this.repo.listAll();
          return pool.some(r => r.avrNumber === candidate);
        }
      });
    }
    return `${prefix}${String(count + 1).padStart(4, "0")}`;
  }
}
